import * as Sentry from '@sentry/angular';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../../environments/environment';

/** GA4 property. One gtag/js load serves every id configured below. */
const GA4_MEASUREMENT_ID = 'G-43GG48V9RH';

/**
 * Google Ads account behind the paid campaigns pointed at /sales-forecast/*.
 *
 * Configured on every page, not just the ones that convert: this is what
 * turns the `gclid` on the ad's landing URL into the first-party `_gcl_aw`
 * cookie. The purchase happens later, on another page and in another tab
 * (Stripe is opened with window.open), and that cookie is the only thing
 * tying the two together.
 */
export const GOOGLE_ADS_ID = 'AW-18370774239';

export const ANALYTICS_DEBUG_KEY = 'analytics_debug';

/**
 * Whether this browser has opted into loading the analytics tags outside
 * production — the only way to verify a conversion without spending on ads.
 *
 * `?analytics=debug` turns it on and it then persists, because the tags load
 * once at app start and the URL that enabled them is gone by the next
 * navigation. `?analytics=off` clears it.
 *
 * @param search defaults to the current query string; passed explicitly in
 *   tests, and the whole thing is guarded because localStorage throws in
 *   some privacy modes.
 */
export function analyticsDebugEnabled(
  search: string = typeof location === 'undefined' ? '' : location.search
): boolean {
  try {
    const requested = new URLSearchParams(search).get('analytics');
    if (requested === 'debug')
      localStorage.setItem(ANALYTICS_DEBUG_KEY, 'true');
    if (requested === 'off') localStorage.removeItem(ANALYTICS_DEBUG_KEY);
    return localStorage.getItem(ANALYTICS_DEBUG_KEY) === 'true';
  } catch {
    return false;
  }
}

@Injectable({ providedIn: 'root' })
export class IntegrationsService {
  private browser = false;
  private gaReady = false;
  private hotjarReady = false;

  constructor(@Inject(PLATFORM_ID) platformId: Object) {
    this.browser = isPlatformBrowser(platformId);
  }

  /**
   * Longest the app may wait on third-party analytics before it renders.
   *
   * Generous on purpose: tripping it early would start dropping events, since
   * trackGA() no-ops until gtag exists. It is a ceiling for the case where
   * something is broken, not a target.
   */
  private static INIT_DEADLINE_MS = 5_000;

  /**
   * Bootstrap is gated on this promise (see the APP_INITIALIZER in main.ts),
   * so it must always settle. Two things in here otherwise never do:
   * `load` may not fire if a subresource stalls, and a script that is dropped
   * rather than refused (DNS blackhole, captive portal, a filter that answers
   * with nothing) fires neither onload nor onerror — a hang is not a
   * rejection, so no try/catch can reach it.
   *
   * Past the deadline the app renders and the loading continues in the
   * background, so a slow tag costs a few early events rather than the page.
   */
  async initAll() {
    if (!this.browser) return;
    if (!environment.production && !analyticsDebugEnabled()) return;

    await this.withDeadline(
      this.loadIntegrations(),
      IntegrationsService.INIT_DEADLINE_MS
    );
  }

  private async loadIntegrations(): Promise<void> {
    await this.waitForLoad();

    await Promise.all([this.initHotjar(), this.initGA()]);
  }

  private withDeadline(work: Promise<void>, ms: number): Promise<void> {
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        console.error(
          `Analytics did not finish loading within ${ms}ms — rendering anyway`
        );
        resolve();
      }, ms);

      // Both settle paths clear the timer: initGA and initHotjar catch their
      // own failures, but a future integration must not be able to leave the
      // app waiting either.
      const done = () => {
        clearTimeout(timer);
        resolve();
      };
      work.then(done, done);
    });
  }

  /**
   * Same try/catch as initHotjar, and for the same reason: initAll() is what
   * an APP_INITIALIZER returns, so an uncaught rejection here aborts
   * bootstrapApplication and the visitor never gets past the preloader.
   * googletagmanager.com is blocked by every common ad blocker, which made
   * analytics a hard dependency of the app rendering at all.
   *
   * console.debug, not console.error: Sentry's captureConsoleIntegration
   * reports errors, and a blocked tag is an expected condition for a large
   * share of visitors, not an exception worth paging anyone about.
   */
  private async initGA() {
    if (this.gaReady) return;

    try {
      (window as any).dataLayer = (window as any).dataLayer || [];
      const gtag = function () {
        (window as any).dataLayer.push(arguments);
      };
      (window as any).gtag = gtag;

      await this.appendScript(
        `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`,
        { async: true }
      );
      // @ts-ignore
      gtag('js', new Date());
      // @ts-ignore
      gtag('config', GA4_MEASUREMENT_ID);
      // @ts-ignore
      gtag('config', GOOGLE_ADS_ID);
      this.gaReady = true;
    } catch (e) {
      console.error('Google Analytics failed to load (likely blocked):', e);
    }
  }

  private async initHotjar() {
    if (this.hotjarReady) return;

    try {
      const w = window as any;

      w.hj =
        w.hj ||
        function () {
          (w.hj.q = w.hj.q || []).push(arguments);
        };
      w._hjSettings = { hjid: 3729323, hjsv: 6 };

      const src = `https://static.hotjar.com/c/hotjar-${w._hjSettings.hjid}.js?sv=${w._hjSettings.hjsv}`;
      await this.appendScript(src, {
        async: true,
        crossOrigin: 'anonymous' as any,
      });

      this.hotjarReady = true;
    } catch (e) {
      console.error('Hotjar failed to load (likely blocked):', e);
    }
  }

  private appendScript(
    src: string,
    attrs: Partial<HTMLScriptElement> = {}
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = src;
      Object.assign(s, attrs);
      s.onload = () => resolve();
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  private waitForLoad(): Promise<void> {
    if (document.readyState === 'complete') return Promise.resolve();
    return new Promise((res) =>
      window.addEventListener('load', () => res(), { once: true })
    );
  }

  track(
    event: string,
    params?: Record<string, any>,
    opts: { ga?: boolean } = { ga: true }
  ) {
    if (opts.ga) this.trackGA(event, params);
  }

  trackGA(event: string, params?: Record<string, any>) {
    if (!(window as any).gtag) return;
    (window as any).gtag('event', event, params);
  }
}

export function handleIntegrations(): void {
  initSentry();
}

function initSentry(): void {
  Sentry.init({
    dsn: 'https://60236da900aa8e3fd1eba789807da88e@o4505992663793664.ingest.sentry.io/4505993237692416',
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
      Sentry.captureConsoleIntegration({ levels: ['error'] }),
    ],
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ['localhost', 'https://interactive-map-ai.com/'],
    // Performance Monitoring
    tracesSampleRate: 1.0, // Capture 100% of the transactions, reduce in production!
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
}
